import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Check, ChevronLeft, ChevronRight, Upload, Loader2, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { uploadFile } from '@/services/uploadService';
import {
  validateEIN,
  validateZipCode,
  handleNumericInput,
  US_STATES
} from '@/utils/validators';

const steps = [
  { id: 1, name: 'Business Info', description: 'Company details' },
  { id: 2, name: 'Financial Details', description: 'Income & expenses' },
  { id: 3, name: 'Deductions', description: 'Business deductions' },
  { id: 4, name: 'Upload Documents', description: 'Required documents' },
  { id: 5, name: 'Review & Submit', description: 'Final review' },
];

export default function BusinessFilingWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filingId = searchParams.get('id');
  const viewMode = searchParams.get('mode') === 'view';
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(!!filingId);
  const [existingFilingId, setExistingFilingId] = useState(filingId);
  const [filingStatus, setFilingStatus] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null); // Track which doc is uploading
  const [formData, setFormData] = useState({
    // Step 1 - Business Info
    businessName: '',
    ein: '',
    businessType: '',
    industryType: '',
    businessAddress: '',
    businessCity: '',
    businessState: '',
    businessZip: '',
    accountingMethod: '',
    fiscalYearEnd: '',
    // Step 2 - Financial Details
    grossReceipts: '',
    costOfGoodsSold: '',
    otherIncome: '',
    advertising: '',
    carExpenses: '',
    insurance: '',
    utilities: '',
    wages: '',
    // Step 3 - Deductions
    homeOffice: '',
    depreciation: '',
    businessMeals: '',
    travel: '',
    professionalFees: '',
    // Step 4 - Documents (stored with file URLs from B2)
    documents: {}, // { profitLoss: { name, url, id }, balanceSheet: {...}, ... }
    otherDocuments: [], // Array for multiple other documents
  });

  // Check if form should be read-only (ready for review, submitted to IRS - but NOT submitted, as clients can still edit)
  const isReadOnly = filingStatus && !['DRAFT', 'NEED_INFO', 'SUBMITTED'].includes(filingStatus);

  // Load existing filing if ID is provided
  useEffect(() => {
    const loadFiling = async () => {
      const isNewFiling = searchParams.get('new') === 'true';
      
      // If explicitly creating new filing, don't redirect to existing
      if (isNewFiling) {
        setLoading(false);
        return;
      }
      
      if (!filingId) {
        // No ID and not explicitly new - just show empty form
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const backendUrl = getBackendUrl();
        const token = localStorage.getItem('access_token');
        
        const response = await axios.get(`${backendUrl}/api/tax-filings/${filingId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const filing = response.data;
        setExistingFilingId(filing.id);
        setFilingStatus(filing.status);
        
        // Populate form with existing data
        const personalInfo = filing.personal_info || {};
        const incomeDetails = filing.income_details || {};
        const deductions = filing.deductions || {};
        
        setFormData({
          businessName: personalInfo.business_name || '',
          ein: personalInfo.ein || '',
          businessType: personalInfo.business_type || '',
          industryType: personalInfo.industry_type || '',
          businessAddress: personalInfo.business_address || '',
          businessCity: personalInfo.business_city || '',
          businessState: personalInfo.business_state || '',
          businessZip: personalInfo.business_zip || '',
          accountingMethod: personalInfo.accounting_method || '',
          fiscalYearEnd: personalInfo.fiscal_year_end || '',
          grossReceipts: incomeDetails.gross_receipts || '',
          costOfGoodsSold: incomeDetails.cost_of_goods_sold || '',
          otherIncome: incomeDetails.other_income || '',
          advertising: deductions.advertising || '',
          carExpenses: deductions.car_expenses || '',
          insurance: deductions.insurance || '',
          utilities: deductions.utilities || '',
          wages: deductions.wages || '',
          homeOffice: deductions.home_office || '',
          depreciation: deductions.depreciation || '',
          businessMeals: deductions.business_meals || '',
          travel: deductions.travel || '',
          professionalFees: deductions.professional_fees || '',
          documents: personalInfo.documents || {},
          otherDocuments: personalInfo.otherDocuments || [],
        });
        
        toast.success('Loaded existing filing');
      } catch (error) {
        console.error('Error loading filing:', error);
        toast.error('Failed to load filing');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadFiling();
    }
  }, [user, filingId, navigate]);

  const progress = (currentStep / steps.length) * 100;

  // Address autocomplete functionality removed

  const validateCurrentStep = () => {
    const errors = {};

    // Step 1 - Business Information Validation
    if (currentStep === 1) {
      // Business Name
      if (!formData.businessName || formData.businessName.trim() === '') {
        errors.businessName = 'Business name is required';
      }

      // EIN Validation
      const einValidation = validateEIN(formData.ein);
      if (!einValidation.isValid) {
        errors.ein = einValidation.error;
      }

      // Business Type
      if (!formData.businessType) {
        errors.businessType = 'Business type is required';
      }

      // Industry Type
      if (!formData.industryType) {
        errors.industryType = 'Industry type is required';
      }

      // Address
      if (!formData.businessAddress || formData.businessAddress.trim() === '') {
        errors.businessAddress = 'Business address is required';
      }

      // City
      if (!formData.businessCity || formData.businessCity.trim() === '') {
        errors.businessCity = 'City is required';
      }

      // State
      if (!formData.businessState) {
        errors.businessState = 'State is required';
      }

      // ZIP Code
      const zipValidation = validateZipCode(formData.businessZip);
      if (!zipValidation.isValid) {
        errors.businessZip = zipValidation.error;
      }

      // Accounting Method
      if (!formData.accountingMethod) {
        errors.accountingMethod = 'Accounting method is required';
      }

      // Fiscal Year End
      if (!formData.fiscalYearEnd || formData.fiscalYearEnd.trim() === '') {
        errors.fiscalYearEnd = 'Fiscal year end is required';
      }
    }

    // Step 2 - Financial Details Validation
    if (currentStep === 2) {
      // Gross Receipts (REQUIRED)
      if (!formData.grossReceipts || formData.grossReceipts === '' || formData.grossReceipts === null || formData.grossReceipts === undefined) {
        errors.grossReceipts = 'Gross receipts/sales is required';
      } else if (parseFloat(formData.grossReceipts) < 0) {
        errors.grossReceipts = 'Amount cannot be negative';
      }

      // Cost of Goods Sold (optional but if entered, must be valid)
      if (formData.costOfGoodsSold && formData.costOfGoodsSold !== '' && parseFloat(formData.costOfGoodsSold) < 0) {
        errors.costOfGoodsSold = 'Amount cannot be negative';
      }
      
      // Other Income (optional but if entered, must be valid)
      if (formData.otherIncome && formData.otherIncome !== '' && parseFloat(formData.otherIncome) < 0) {
        errors.otherIncome = 'Amount cannot be negative';
      }
      
      // All expense fields (optional but if entered, must be valid)
      const expenseFields = ['advertising', 'carExpenses', 'insurance', 'utilities', 'wages'];
      expenseFields.forEach(field => {
        if (formData[field] && formData[field] !== '' && parseFloat(formData[field]) < 0) {
          errors[field] = 'Amount cannot be negative';
        }
      });
    }
    
    // Step 3 - Deductions Validation (all optional but validate if entered)
    if (currentStep === 3) {
      const deductionFields = ['homeOffice', 'depreciation', 'businessMeals', 'travel', 'professionalFees'];
      deductionFields.forEach(field => {
        if (formData[field] && formData[field] !== '' && parseFloat(formData[field]) < 0) {
          errors[field] = 'Amount cannot be negative';
        }
      });
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    // Validate current step before proceeding
    if (!validateCurrentStep()) {
      toast.error('Please fix all validation errors before proceeding');
      window.scrollTo(0, 0);
      return;
    }

    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    } else {
      // Submit to backend
      await handleSubmit();
    }
  };

  const saveDraft = async () => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');

      const filingData = {
        tax_year: new Date().getFullYear().toString(),
        filing_type: 'business',
        status: 'DRAFT',
        personal_info: {
          business_name: formData.businessName,
          ein: formData.ein,
          business_type: formData.businessType,
          industry_type: formData.industryType,
          business_address: formData.businessAddress,
          business_city: formData.businessCity,
          business_state: formData.businessState,
          business_zip: formData.businessZip,
          accounting_method: formData.accountingMethod,
          fiscal_year_end: formData.fiscalYearEnd,
          documents: formData.documents || {},
          otherDocuments: formData.otherDocuments || []
        },
        income_details: {
          gross_receipts: formData.grossReceipts,
          cost_of_goods_sold: formData.costOfGoodsSold,
          other_income: formData.otherIncome
        },
        deductions: {
          advertising: formData.advertising,
          car_expenses: formData.carExpenses,
          insurance: formData.insurance,
          utilities: formData.utilities,
          wages: formData.wages,
          home_office: formData.homeOffice,
          depreciation: formData.depreciation,
          business_meals: formData.businessMeals,
          travel: formData.travel,
          professional_fees: formData.professionalFees
        }
      };

      if (existingFilingId) {
        // Update existing filing
        await axios.patch(`${backendUrl}/api/tax-filings/${existingFilingId}`, filingData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new filing
        const response = await axios.post(`${backendUrl}/api/tax-filings`, filingData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setExistingFilingId(response.data.id);
      }

      toast.success('Draft saved successfully!');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft. Please try again.');
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');

      const filingData = {
        tax_year: new Date().getFullYear().toString(),
        filing_type: 'business',
        status: 'SUBMITTED',
        personal_info: {
          business_name: formData.businessName,
          ein: formData.ein,
          business_type: formData.businessType,
          industry_type: formData.industryType,
          business_address: formData.businessAddress,
          business_city: formData.businessCity,
          business_state: formData.businessState,
          business_zip: formData.businessZip,
          accounting_method: formData.accountingMethod,
          fiscal_year_end: formData.fiscalYearEnd,
          documents: formData.documents || {},
          otherDocuments: formData.otherDocuments || []
        },
        income_details: {
          gross_receipts: formData.grossReceipts,
          cost_of_goods_sold: formData.costOfGoodsSold,
          other_income: formData.otherIncome
        },
        deductions: {
          advertising: formData.advertising,
          car_expenses: formData.carExpenses,
          insurance: formData.insurance,
          utilities: formData.utilities,
          wages: formData.wages,
          home_office: formData.homeOffice,
          depreciation: formData.depreciation,
          business_meals: formData.businessMeals,
          travel: formData.travel,
          professional_fees: formData.professionalFees
        }
      };

      if (existingFilingId) {
        // Update existing filing
        await axios.patch(`${backendUrl}/api/tax-filings/${existingFilingId}`, filingData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new filing
        await axios.post(`${backendUrl}/api/tax-filings`, filingData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast.success('Business tax filing submitted successfully!');
      navigate('/business/dashboard');
    } catch (error) {
      console.error('Error submitting business filing:', error);
      toast.error('Failed to submit filing. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const updateFormData = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="rounded-lg border-l-4 border-l-primary bg-primary/5 p-4">
              <p className="text-sm font-medium text-primary">Business Information</p>
              <p className="text-sm text-muted-foreground mt-1">
                Provide your business details as registered with the IRS
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="businessName">Business Legal Name *</Label>
                <Input
                  id="businessName"
                  value={formData.businessName}
                  disabled={isReadOnly} onChange={(e) => updateFormData('businessName', e.target.value)}
                  placeholder="ABC Consulting LLC"
                  required
                />
                {validationErrors.businessName && (
                  <p className="text-xs text-red-500">{validationErrors.businessName}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="ein">EIN (Employer Identification Number) *</Label>
                <Input
                  id="ein"
                  value={formData.ein}
                  disabled={isReadOnly} onChange={(e) => updateFormData('ein', handleNumericInput(e.target.value, 9))}
                  placeholder="123456789"
                  maxLength={9}
                  required
                />
                <p className="text-xs text-muted-foreground">9 digits, numbers only</p>
                {validationErrors.ein && (
                  <p className="text-xs text-red-500">{validationErrors.ein}</p>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="businessType">Business Type *</Label>
                <Select value={formData.businessType} disabled={isReadOnly} onValueChange={(value) => updateFormData('businessType', value)}>
                  <SelectTrigger id="businessType">
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sole_proprietorship">Sole Proprietorship (Schedule C)</SelectItem>
                    <SelectItem value="llc">LLC</SelectItem>
                    <SelectItem value="s_corp">S-Corporation</SelectItem>
                    <SelectItem value="c_corp">C-Corporation</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                  </SelectContent>
                </Select>
                {validationErrors.businessType && (
                  <p className="text-xs text-red-500">{validationErrors.businessType}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="industryType">Industry Type *</Label>
                <Select value={formData.industryType} disabled={isReadOnly} onValueChange={(value) => updateFormData('industryType', value)}>
                  <SelectTrigger id="industryType">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consulting">Consulting</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="technology">Technology</SelectItem>
                    <SelectItem value="real_estate">Real Estate</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="construction">Construction</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {validationErrors.industryType && (
                  <p className="text-xs text-red-500">{validationErrors.industryType}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessAddress">Business Address *</Label>
              <Input
                id="businessAddress"
                value={formData.businessAddress}
                disabled={isReadOnly} onChange={(e) => updateFormData('businessAddress', e.target.value)}
                placeholder="123 Main Street"
                required
              />
              {validationErrors.businessAddress && (
                <p className="text-xs text-red-500">{validationErrors.businessAddress}</p>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="businessCity">City *</Label>
                <Input
                  id="businessCity"
                  value={formData.businessCity}
                  disabled={isReadOnly} onChange={(e) => updateFormData('businessCity', e.target.value)}
                  placeholder="New York"
                  required
                />
                {validationErrors.businessCity && (
                  <p className="text-xs text-red-500">{validationErrors.businessCity}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="businessState">State *</Label>
                <Select value={formData.businessState} disabled={isReadOnly} onValueChange={(value) => updateFormData('businessState', value)}>
                  <SelectTrigger id="businessState">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {US_STATES.map((state) => (
                      <SelectItem key={state.value} value={state.value}>
                        {state.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {validationErrors.businessState && (
                  <p className="text-xs text-red-500">{validationErrors.businessState}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="businessZip">ZIP Code *</Label>
                <Input
                  id="businessZip"
                  value={formData.businessZip}
                  disabled={isReadOnly} onChange={(e) => updateFormData('businessZip', handleNumericInput(e.target.value, 5))}
                  placeholder="10001"
                  maxLength={5}
                  required
                />
                <p className="text-xs text-muted-foreground">5 digits</p>
                {validationErrors.businessZip && (
                  <p className="text-xs text-red-500">{validationErrors.businessZip}</p>
                )}
              </div>
            </div>

            {/* Address verification removed */}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="accountingMethod">Accounting Method *</Label>
                <Select value={formData.accountingMethod} disabled={isReadOnly} onValueChange={(value) => updateFormData('accountingMethod', value)}>
                  <SelectTrigger id="accountingMethod">
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="accrual">Accrual</SelectItem>
                  </SelectContent>
                </Select>
                {validationErrors.accountingMethod && (
                  <p className="text-xs text-red-500">{validationErrors.accountingMethod}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="fiscalYearEnd">Fiscal Year End *</Label>
                <Input
                  id="fiscalYearEnd"
                  type="date"
                  value={formData.fiscalYearEnd}
                  disabled={isReadOnly} onChange={(e) => updateFormData('fiscalYearEnd', e.target.value)}
                  required
                />
                {validationErrors.fiscalYearEnd && (
                  <p className="text-xs text-red-500">{validationErrors.fiscalYearEnd}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="rounded-lg border-l-4 border-l-primary bg-primary/5 p-4">
              <p className="text-sm font-medium text-primary">Financial Information</p>
              <p className="text-sm text-muted-foreground mt-1">
                Enter your business income and expenses
              </p>
            </div>

            <Card className="tax-card">
              <CardHeader>
                <CardTitle className="text-lg">Business Income</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="grossReceipts">Gross Receipts/Sales *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      id="grossReceipts"
                      type="number"
                      value={formData.grossReceipts}
                      disabled={isReadOnly} onChange={(e) => updateFormData('grossReceipts', e.target.value)}
                      placeholder="250000"
                      className="pl-7"
                      min="0"
                      required
                    />
                  </div>
                  {validationErrors.grossReceipts && (
                    <p className="text-xs text-red-500">{validationErrors.grossReceipts}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="costOfGoodsSold">Cost of Goods Sold</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      id="costOfGoodsSold"
                      type="number"
                      value={formData.costOfGoodsSold}
                      disabled={isReadOnly} onChange={(e) => updateFormData('costOfGoodsSold', e.target.value)}
                      placeholder="50000"
                      className="pl-7"
                      min="0"
                    />
                  </div>
                  {validationErrors.costOfGoodsSold && (
                    <p className="text-xs text-red-500">{validationErrors.costOfGoodsSold}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="otherIncome">Other Business Income</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      id="otherIncome"
                      type="number"
                      value={formData.otherIncome}
                      disabled={isReadOnly} onChange={(e) => updateFormData('otherIncome', e.target.value)}
                      placeholder="10000"
                      className="pl-7"
                      min="0"
                    />
                  </div>
                  {validationErrors.otherIncome && (
                    <p className="text-xs text-red-500">{validationErrors.otherIncome}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="tax-card">
              <CardHeader>
                <CardTitle className="text-lg">Business Expenses</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="advertising">Advertising & Marketing</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        id="advertising"
                        type="number"
                        value={formData.advertising}
                        disabled={isReadOnly} onChange={(e) => updateFormData('advertising', e.target.value)}
                        placeholder="5000"
                        className="pl-7"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="carExpenses">Car & Vehicle Expenses</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        id="carExpenses"
                        type="number"
                        value={formData.carExpenses}
                        disabled={isReadOnly} onChange={(e) => updateFormData('carExpenses', e.target.value)}
                        placeholder="8000"
                        className="pl-7"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="insurance">Business Insurance</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        id="insurance"
                        type="number"
                        value={formData.insurance}
                        disabled={isReadOnly} onChange={(e) => updateFormData('insurance', e.target.value)}
                        placeholder="12000"
                        className="pl-7"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="utilities">Utilities</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        id="utilities"
                        type="number"
                        value={formData.utilities}
                        disabled={isReadOnly} onChange={(e) => updateFormData('utilities', e.target.value)}
                        placeholder="3000"
                        className="pl-7"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="wages">Wages & Payroll</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        id="wages"
                        type="number"
                        value={formData.wages}
                        disabled={isReadOnly} onChange={(e) => updateFormData('wages', e.target.value)}
                        placeholder="80000"
                        className="pl-7"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Enter additional business deductions to reduce your tax liability.
            </p>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="homeOffice">Home Office Deduction</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="homeOffice"
                    type="number"
                    value={formData.homeOffice}
                    disabled={isReadOnly} onChange={(e) => updateFormData('homeOffice', e.target.value)}
                    placeholder="5000"
                    className="pl-7"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="depreciation">Depreciation</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="depreciation"
                    type="number"
                    value={formData.depreciation}
                    disabled={isReadOnly} onChange={(e) => updateFormData('depreciation', e.target.value)}
                    placeholder="15000"
                    className="pl-7"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="businessMeals">Business Meals (50%)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="businessMeals"
                    type="number"
                    value={formData.businessMeals}
                    disabled={isReadOnly} onChange={(e) => updateFormData('businessMeals', e.target.value)}
                    placeholder="4000"
                    className="pl-7"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="travel">Business Travel</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="travel"
                    type="number"
                    value={formData.travel}
                    disabled={isReadOnly} onChange={(e) => updateFormData('travel', e.target.value)}
                    placeholder="6000"
                    className="pl-7"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="professionalFees">Professional Fees</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="professionalFees"
                    type="number"
                    value={formData.professionalFees}
                    disabled={isReadOnly} onChange={(e) => updateFormData('professionalFees', e.target.value)}
                    placeholder="8000"
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Legal, accounting, consulting fees
                </p>
              </div>
            </div>
          </div>
        );

      case 4:
        // Handle business file upload to B2 storage
        const handleBusinessFileUpload = (docType) => async (event) => {
          const file = event.target.files[0];
          if (!file) return;

          // Validate file type
          const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
          if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload PDF, JPG, PNG, or Excel files only');
            return;
          }

          // Validate file size (max 10MB)
          if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
          }

          // Need a filing ID to upload - save draft first if not exists
          let currentFilingId = existingFilingId;
          if (!currentFilingId) {
            try {
              setUploadingDoc(docType);
              const backendUrl = getBackendUrl();
              const token = localStorage.getItem('access_token');
              
              // Create a draft filing first
              const draftData = {
                tax_year: new Date().getFullYear().toString(),
                filing_type: 'business',
                status: 'DRAFT',
                personal_info: {
                  business_name: formData.businessName || 'Draft Business Filing',
                }
              };
              
              const response = await axios.post(`${backendUrl}/api/tax-filings`, draftData, {
                headers: { Authorization: `Bearer ${token}` }
              });
              currentFilingId = response.data.id;
              setExistingFilingId(currentFilingId);
            } catch (err) {
              console.error('Error creating draft for upload:', err);
              toast.error('Failed to initialize upload. Please try again.');
              setUploadingDoc(null);
              return;
            }
          }

          // Upload file to B2 via uploadService
          try {
            setUploadingDoc(docType);
            const result = await uploadFile(
              file,
              'tax_filing',
              currentFilingId,
              `business_${docType}`,
              null
            );

            if (result.success) {
              // Store uploaded file info
              updateFormData('documents', {
                ...formData.documents,
                [docType]: {
                  name: file.name,
                  url: result.data.file_url,
                  id: result.data.id,
                  uploaded: true
                }
              });
              
              // Save the filing to persist the document info
              const backendUrl = getBackendUrl();
              const token = localStorage.getItem('access_token');
              const updatedDocs = {
                ...formData.documents,
                [docType]: {
                  name: file.name,
                  url: result.data.file_url,
                  id: result.data.id,
                  uploaded: true
                }
              };
              await axios.patch(`${backendUrl}/api/tax-filings/${currentFilingId}`, {
                personal_info: {
                  ...formData,
                  documents: updatedDocs
                }
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              
              toast.success(`${file.name} uploaded successfully`);
            } else {
              toast.error(result.error || 'Upload failed');
            }
          } catch (err) {
            console.error('Error uploading file:', err);
            toast.error('Failed to upload file');
          } finally {
            setUploadingDoc(null);
            event.target.value = ''; // Reset input
          }
        };

        // Handle other documents upload (multiple files)
        const handleOtherDocsUpload = async (event) => {
          const files = Array.from(event.target.files);
          if (files.length === 0) return;

          let currentFilingId = existingFilingId;
          if (!currentFilingId) {
            try {
              setUploadingDoc('other');
              const backendUrl = getBackendUrl();
              const token = localStorage.getItem('access_token');
              
              const draftData = {
                tax_year: new Date().getFullYear().toString(),
                filing_type: 'business',
                status: 'DRAFT',
                personal_info: {
                  business_name: formData.businessName || 'Draft Business Filing',
                }
              };
              
              const response = await axios.post(`${backendUrl}/api/tax-filings`, draftData, {
                headers: { Authorization: `Bearer ${token}` }
              });
              currentFilingId = response.data.id;
              setExistingFilingId(currentFilingId);
            } catch (err) {
              console.error('Error creating draft for upload:', err);
              toast.error('Failed to initialize upload. Please try again.');
              setUploadingDoc(null);
              return;
            }
          }

          setUploadingDoc('other');
          const newDocs = [...(formData.otherDocuments || [])];

          for (const file of files) {
            // Validate
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
            if (!allowedTypes.includes(file.type)) {
              toast.error(`${file.name}: Invalid file type`);
              continue;
            }
            if (file.size > 10 * 1024 * 1024) {
              toast.error(`${file.name}: File too large (max 10MB)`);
              continue;
            }

            try {
              const result = await uploadFile(
                file,
                'tax_filing',
                currentFilingId,
                'business_other',
                null
              );

              if (result.success) {
                newDocs.push({
                  name: file.name,
                  url: result.data.file_url,
                  id: result.data.id,
                  uploaded: true
                });
              } else {
                toast.error(`${file.name}: ${result.error || 'Upload failed'}`);
              }
            } catch (err) {
              console.error('Error uploading:', err);
              toast.error(`${file.name}: Upload failed`);
            }
          }

          updateFormData('otherDocuments', newDocs);
          
          // Save the filing to persist the document info
          if (newDocs.length > (formData.otherDocuments?.length || 0)) {
            try {
              const backendUrl = getBackendUrl();
              const token = localStorage.getItem('access_token');
              await axios.patch(`${backendUrl}/api/tax-filings/${currentFilingId}`, {
                personal_info: {
                  ...formData,
                  otherDocuments: newDocs
                }
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              toast.success('Documents uploaded successfully');
            } catch (err) {
              console.error('Error saving documents:', err);
            }
          }
          
          setUploadingDoc(null);
          event.target.value = '';
        };

        // Remove other document
        const handleRemoveOtherDoc = (index) => {
          const newDocs = formData.otherDocuments.filter((_, i) => i !== index);
          updateFormData('otherDocuments', newDocs);
          toast.success('Document removed');
        };

        // Helper to get document display name
        const getDocDisplay = (docType) => {
          const doc = formData.documents?.[docType];
          if (!doc) return null;
          // Handle both old format (string) and new format (object)
          if (typeof doc === 'string') return doc;
          return doc.name;
        };

        return (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              {isReadOnly 
                ? 'Uploaded documents for this filing:'
                : 'Upload all required business tax documents. Accepted formats: PDF, JPG, PNG, Excel (Max 10MB each)'}
            </p>

            <div className="grid gap-4 md:grid-cols-2">
              <Card className="tax-card">
                <CardHeader>
                  <CardTitle className="text-base">Profit & Loss Statement</CardTitle>
                  <CardDescription>Year-end P&L required</CardDescription>
                </CardHeader>
                <CardContent>
                  {!isReadOnly && (
                    <input
                      type="file"
                      id="pl-upload"
                      accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                      className="hidden"
                      onChange={handleBusinessFileUpload('profitLoss')}
                    />
                  )}
                  {!isReadOnly && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => document.getElementById('pl-upload').click()}
                      disabled={uploadingDoc === 'profitLoss'}
                    >
                      {uploadingDoc === 'profitLoss' ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-2" /> {getDocDisplay('profitLoss') ? 'Replace P&L' : 'Upload P&L'}</>
                      )}
                    </Button>
                  )}
                  {getDocDisplay('profitLoss') ? (
                    <p className="text-xs text-green-600 mt-2">✓ {getDocDisplay('profitLoss')}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-2">No document uploaded</p>
                  )}
                </CardContent>
              </Card>

              <Card className="tax-card">
                <CardHeader>
                  <CardTitle className="text-base">Balance Sheet</CardTitle>
                  <CardDescription>Year-end balance sheet</CardDescription>
                </CardHeader>
                <CardContent>
                  {!isReadOnly && (
                    <input
                      type="file"
                      id="balance-upload"
                      accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                      className="hidden"
                      onChange={handleBusinessFileUpload('balanceSheet')}
                    />
                  )}
                  {!isReadOnly && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => document.getElementById('balance-upload').click()}
                      disabled={uploadingDoc === 'balanceSheet'}
                    >
                      {uploadingDoc === 'balanceSheet' ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-2" /> {getDocDisplay('balanceSheet') ? 'Replace Balance Sheet' : 'Upload Balance Sheet'}</>
                      )}
                    </Button>
                  )}
                  {getDocDisplay('balanceSheet') ? (
                    <p className="text-xs text-green-600 mt-2">✓ {getDocDisplay('balanceSheet')}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-2">No document uploaded</p>
                  )}
                </CardContent>
              </Card>

              <Card className="tax-card">
                <CardHeader>
                  <CardTitle className="text-base">Payroll Documents</CardTitle>
                  <CardDescription>If you have employees</CardDescription>
                </CardHeader>
                <CardContent>
                  {!isReadOnly && (
                    <input
                      type="file"
                      id="payroll-upload"
                      accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                      className="hidden"
                      onChange={handleBusinessFileUpload('payroll')}
                    />
                  )}
                  {!isReadOnly && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => document.getElementById('payroll-upload').click()}
                      disabled={uploadingDoc === 'payroll'}
                    >
                      {uploadingDoc === 'payroll' ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-2" /> {getDocDisplay('payroll') ? 'Replace Payroll Docs' : 'Upload Payroll Docs'}</>
                      )}
                    </Button>
                  )}
                  {getDocDisplay('payroll') ? (
                    <p className="text-xs text-green-600 mt-2">✓ {getDocDisplay('payroll')}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-2">No document uploaded</p>
                  )}
                </CardContent>
              </Card>

              <Card className="tax-card">
                <CardHeader>
                  <CardTitle className="text-base">1099 Contractor List</CardTitle>
                  <CardDescription>Independent contractors paid</CardDescription>
                </CardHeader>
                <CardContent>
                  {!isReadOnly && (
                    <input
                      type="file"
                      id="1099-upload"
                      accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                      className="hidden"
                      onChange={handleBusinessFileUpload('contractors1099')}
                    />
                  )}
                  {!isReadOnly && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => document.getElementById('1099-upload').click()}
                      disabled={uploadingDoc === 'contractors1099'}
                    >
                      {uploadingDoc === 'contractors1099' ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-2" /> {getDocDisplay('contractors1099') ? 'Replace 1099 List' : 'Upload 1099 List'}</>
                      )}
                    </Button>
                  )}
                  {getDocDisplay('contractors1099') ? (
                    <p className="text-xs text-green-600 mt-2">✓ {getDocDisplay('contractors1099')}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-2">No document uploaded</p>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card className="tax-card">
              <CardHeader>
                <CardTitle className="text-base">Other Documents</CardTitle>
                <CardDescription>Upload any additional supporting documents</CardDescription>
              </CardHeader>
              <CardContent>
                {!isReadOnly && (
                  <div 
                    className={cn(
                      "border-2 border-dashed rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer bg-muted/30 mb-4",
                      uploadingDoc === 'other' && "opacity-50 pointer-events-none"
                    )}
                    onClick={() => document.getElementById('other-business-docs-upload').click()}
                  >
                    <input
                      type="file"
                      id="other-business-docs-upload"
                      accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                      className="hidden"
                      multiple
                      onChange={handleOtherDocsUpload}
                    />
                    {uploadingDoc === 'other' ? (
                      <>
                        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-primary" />
                        <p className="text-sm font-medium">Uploading documents...</p>
                      </>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm font-medium">Click to upload additional documents</p>
                        <p className="text-xs text-muted-foreground">PDF, JPG, PNG, Excel files (Max 10MB each)</p>
                      </>
                    )}
                  </div>
                )}
                
                {formData.otherDocuments && formData.otherDocuments.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium mb-2">Uploaded Documents ({formData.otherDocuments.length})</p>
                    {formData.otherDocuments.map((doc, index) => (
                      <div key={doc.id || index} className="flex items-center justify-between rounded-lg border p-3 bg-muted/50">
                        <div className="flex-1">
                          <p className="text-sm font-medium">{doc.name || doc.file_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {doc.uploaded && <span className="text-green-600">✓ Uploaded to cloud</span>}
                          </p>
                        </div>
                        {!isReadOnly && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleRemoveOtherDoc(index)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <Card className="tax-card">
              <CardHeader>
                <CardTitle>Business Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">Business Name:</span>
                  <span className="font-medium">{formData.businessName || 'Not provided'}</span>
                  <span className="text-muted-foreground">EIN:</span>
                  <span className="font-medium">{formData.ein || 'Not provided'}</span>
                  <span className="text-muted-foreground">Business Type:</span>
                  <span className="font-medium">{formData.businessType || 'Not provided'}</span>
                  <span className="text-muted-foreground">Accounting Method:</span>
                  <span className="font-medium">{formData.accountingMethod || 'Not provided'}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="tax-card">
              <CardHeader>
                <CardTitle>Financial Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">Gross Receipts:</span>
                  <span className="font-medium">${formData.grossReceipts || '0'}</span>
                  <span className="text-muted-foreground">Total Expenses:</span>
                  <span className="font-medium">
                    ${(
                      Number(formData.advertising || 0) +
                      Number(formData.carExpenses || 0) +
                      Number(formData.insurance || 0) +
                      Number(formData.utilities || 0) +
                      Number(formData.wages || 0)
                    ).toLocaleString()}
                  </span>
                  <span className="text-muted-foreground">Net Profit:</span>
                  <span className="font-bold text-primary">
                    ${(
                      Number(formData.grossReceipts || 0) -
                      Number(formData.costOfGoodsSold || 0) -
                      (Number(formData.advertising || 0) +
                      Number(formData.carExpenses || 0) +
                      Number(formData.insurance || 0) +
                      Number(formData.utilities || 0) +
                      Number(formData.wages || 0))
                    ).toLocaleString()}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Tax Liability Estimate
                  </p>
                  <p className="mt-2 text-sm text-blue-600 dark:text-blue-300">
                    Your estimated tax liability will be calculated by our tax experts after reviewing your submission.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="client" title="Business Tax Filing" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="client" title="Business Tax Filing" user={user}>
      <div className="mx-auto max-w-4xl space-y-6">
        <Breadcrumbs items={[
          { label: 'Business', href: '/business/dashboard' },
          { label: 'Tax Filing' }
        ]} />

        {/* Read-only Banner */}
        {isReadOnly && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 p-4">
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              View Only Mode - This filing has been submitted and cannot be edited.
            </p>
            <p className="text-sm text-blue-600 dark:text-blue-300 mt-1">
              Status: {filingStatus?.replace('_', ' ')}
            </p>
          </div>
        )}

        {/* Progress Bar */}
        <Card className="tax-card">
          <CardContent className="pt-6">
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Step {currentStep} of {steps.length}</span>
                <span className="text-muted-foreground">{Math.round(progress)}% Complete</span>
              </div>
              <Progress value={progress} className="mt-2" />
            </div>

            {/* Stepper */}
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div
                      className={cn(
                        'flex h-10 w-10 items-center justify-center rounded-full border-2 font-semibold transition-colors',
                        currentStep > step.id
                          ? 'border-primary bg-primary text-primary-foreground'
                          : currentStep === step.id
                          ? 'border-primary bg-background text-primary'
                          : 'border-muted-foreground/30 bg-background text-muted-foreground'
                      )}
                    >
                      {currentStep > step.id ? (
                        <Check className="h-5 w-5" />
                      ) : (
                        step.id
                      )}
                    </div>
                    <div className="mt-2 hidden text-center md:block">
                      <p className="text-xs font-medium">{step.name}</p>
                      <p className="text-xs text-muted-foreground">{step.description}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={cn(
                        'h-0.5 flex-1 transition-colors',
                        currentStep > step.id ? 'bg-primary' : 'bg-muted-foreground/30'
                      )}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Step Content */}
        <Card className="tax-card">
          <CardHeader>
            <CardTitle>{steps[currentStep - 1].name}</CardTitle>
            <CardDescription>{steps[currentStep - 1].description}</CardDescription>
          </CardHeader>
          <CardContent>{renderStepContent()}</CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1 || submitting}
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>
          <div className="flex gap-2">
            {isReadOnly ? (
              <>
                {currentStep < steps.length && (
                  <Button onClick={() => { setCurrentStep(currentStep + 1); window.scrollTo(0, 0); }}>
                    Next
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
                {currentStep === steps.length && (
                  <Button
                    variant="outline"
                    onClick={() => navigate('/business/dashboard')}
                  >
                    Back to Dashboard
                  </Button>
                )}
              </>
            ) : (
              <>
                {/* Only show Save as Draft button for DRAFT status filings */}
                {(!filingStatus || filingStatus === 'DRAFT') && (
                  <Button
                    variant="outline"
                    onClick={saveDraft}
                    disabled={submitting}
                  >
                    Save as Draft
                  </Button>
                )}
                <Button onClick={handleNext} className="btn-professional" disabled={submitting}>
                  {submitting ? 'Submitting...' : currentStep === steps.length ? 'Submit' : 'Next'}
                  {currentStep < steps.length && !submitting && <ChevronRight className="ml-2 h-4 w-4" />}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
